import Vue from "vue";
import { ThemeConfig, PartialThemeConfig } from "@/config/theme/themeTypes";
import { defaultThemeConfig } from "@/config/theme/defaultThemeConfig";
import { mergeThemeConfig } from "@/helpers/theme/mergeThemeConfig";
import { createThemeCssVars } from "@/helpers/theme/createThemeCssVars";

const state = Vue.observable<{ theme: ThemeConfig }>({
  theme: defaultThemeConfig
});

/**
 * Escribe las CSS vars del theme en `<html>` (document.documentElement).
 *
 * Necesario porque Vuetify 1.5 `<v-dialog>` (y otros menus, tooltips,
 * autocomplete dropdowns) teleportan su contenido fuera del DOM subtree
 * del componente — renderizan directo bajo `<body>`. Por tanto, las
 * vars inyectadas como inline style sobre `<div id="rc-vistaweb">` (en
 * App.vue) NO las heredan los dialogs.
 *
 * Solución: aplicar también las vars en `<html>` para que las herede
 * TODO el DOM, incluyendo elementos teleportados.
 *
 * El inline style del div root se mantiene por redundancia (cero costo,
 * doble cobertura). Cuando ambas vars apuntan al mismo valor el browser
 * usa la más cercana (la del div), pero los teleportados caen en la del
 * `<html>`.
 */
function writeVarsToDocument(theme: ThemeConfig): void {
  if (typeof document === "undefined") return; // SSR safety
  const vars = createThemeCssVars(theme);
  Object.keys(vars).forEach(k => {
    document.documentElement.style.setProperty(k, vars[k]);
  });
}

/**
 * Sincroniza el favicon del DOM con el del theme.
 *
 * El `<link rel="icon">` del index.html es estático y vue-meta v1.6 NO
 * lo adopta para reemplazo (no es SSR — vue-meta sólo gestiona los tags
 * que él mismo crea). Si dejáramos esto a vue-meta, el DOM terminaría
 * con DOS `<link rel="icon">` — el estático y el dinámico — y el
 * navegador prefiere el primero por orden DOM.
 *
 * Solución: tocar el href del tag estático directo desde acá. Mismo
 * patrón imperativo que `loadDynamicScripts` (gtag, GTM, Hotjar).
 */
function writeFaviconToDocument(theme: ThemeConfig): void {
  if (typeof document === "undefined") return; // SSR safety
  const link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!link) return;
  const desired = theme.brand && theme.brand.faviconUrl ? theme.brand.faviconUrl : null;
  if (!desired) return; // sin faviconUrl en el theme dejamos el href que ya tiene el index.html
  if (link.getAttribute("href") !== desired) {
    link.setAttribute("href", desired);
  }
}

// Aplicar vars iniciales al boot (default theme). Los suscriptores de
// applyRemote sobreescribirán cuando el endpoint /theme responda.
writeVarsToDocument(defaultThemeConfig);
writeFaviconToDocument(defaultThemeConfig);

export const themeService = {
  get current(): ThemeConfig {
    return state.theme;
  },
  get cssVars(): Record<string, string> {
    return createThemeCssVars(state.theme);
  },
  applyRemote(partial: PartialThemeConfig | null): void {
    state.theme = mergeThemeConfig(defaultThemeConfig, partial);
    writeVarsToDocument(state.theme);
    writeFaviconToDocument(state.theme);
  },
  reset(): void {
    state.theme = defaultThemeConfig;
    writeVarsToDocument(state.theme);
    writeFaviconToDocument(state.theme);
  }
};
